import { ajaxRequest } from 'utils/ajax-promise';
import { logError, logInfo } from 'fe-lib-logging';

export const AUTH_ROUTE = {
    SIGNUP: 'signup',
    PASSWORD: 'password',
    SSO: 'sso',
    RECOVERY: 'recovery',
};

export const AUTH_OUTCOME = {
    ...AUTH_ROUTE,
    FALLBACK: 'fallback',
};

export const FAILURE_REASON = {
    UNEXPECTED_RESPONSE: 'unexpected response',
    UNSUPPORTED_ROUTE: 'unsupported route',
    REQUEST_FAILED: 'request failed',
    NETWORK_ERROR: 'network error',
    ERROR_HANDLING_RESPONSE: 'error handling response',
    CAPTCHA_TOKEN_UNAVAILABLE: 'captcha token unavailable',
};

const RESOLVE_AUTH_METHOD_URL = '/ajax/auth/resolve-auth-method';

const TELEMETRY_CATEGORY = 'dashboard.signin.authMethodLookup';
const TELEMETRY_ACTION_FAILED = 'authMethodLookupFailed';
const TELEMETRY_ACTION_SUCCEEDED = 'authMethodLookupSucceeded';

const KNOWN_ROUTES = Object.values(AUTH_ROUTE);

function outcomeForRoute(route) {
    return KNOWN_ROUTES.includes(route) ? route : AUTH_OUTCOME.FALLBACK;
}

function recordFailed(reason, detail = null) {
    logError(TELEMETRY_CATEGORY, TELEMETRY_ACTION_FAILED, { reason, detail });
}

function recordSucceeded(authMethod) {
    logInfo(TELEMETRY_CATEGORY, TELEMETRY_ACTION_SUCCEEDED, { authMethod });
}

function buildResult(outcome, route = null, errorCode = null, hasSso = false) {
    return { outcome, route, errorCode, hasSso };
}

function describeUnexpectedResponse(response) {
    return response ? `success: ${response.success}, route: ${response.route}` : 'empty body';
}

function handleResponse(response) {
    if (!response || response.success !== 1 || !response.route) {
        recordFailed(FAILURE_REASON.UNEXPECTED_RESPONSE, describeUnexpectedResponse(response));
        return buildResult(AUTH_OUTCOME.FALLBACK);
    }

    const outcome = outcomeForRoute(response.route);
    if (outcome === AUTH_OUTCOME.FALLBACK) {
        recordFailed(FAILURE_REASON.UNSUPPORTED_ROUTE, response.route);
        return buildResult(AUTH_OUTCOME.FALLBACK, response.route);
    }

    recordSucceeded(outcome);
    return buildResult(outcome, response.route, null, response.hasSso === true);
}

function handleRequestError(error) {
    const errorCode = error?.errorCode;
    if (errorCode) {
        recordFailed(FAILURE_REASON.REQUEST_FAILED, String(errorCode));
    } else {
        recordFailed(FAILURE_REASON.NETWORK_ERROR);
    }
    return buildResult(AUTH_OUTCOME.FALLBACK, null, errorCode);
}

function handleUnexpectedFailure(error) {
    recordFailed(FAILURE_REASON.ERROR_HANDLING_RESPONSE, error?.message ?? String(error));
    return buildResult(AUTH_OUTCOME.FALLBACK);
}

/**
 * Look up which authentication method an email should use so sign-in can route
 * the user without first asking for a password.
 *
 * The endpoint scores `recaptchaToken` with reCAPTCHA v3 and rejects the request without one,
 * so a missing token resolves to `fallback` here rather than spending a round trip.
 */
export const resolveAuthMethod = (email, recaptchaToken) => {
    if (!recaptchaToken) {
        recordFailed(FAILURE_REASON.CAPTCHA_TOKEN_UNAVAILABLE);

        return Promise.resolve(buildResult(AUTH_OUTCOME.FALLBACK));
    }

    return ajaxRequest(RESOLVE_AUTH_METHOD_URL, 'POST', { data: { email, recaptchaToken } })
        .then(handleResponse, handleRequestError)
        .catch(handleUnexpectedFailure)
        .catch(() => buildResult(AUTH_OUTCOME.FALLBACK));
};
